import { z } from 'zod';

// ============================================================
// Meeting Type Detection
// ============================================================

export const MeetingTypeSchema = z.enum([
  'team_standup',    // Réunion d'équipe quotidienne/hebdo
  'client',          // Réunion client
  'brainstorm',      // Session de brainstorming
  'one_on_one',      // 1-on-1 manager/collaborateur
  'project_review',  // Revue de projet
  'decision',        // Réunion de prise de décision
  'general',         // Réunion générale (défaut)
]);

export type MeetingType = z.infer<typeof MeetingTypeSchema>;

// ============================================================
// Priority Levels
// ============================================================

export const PrioritySchema = z.enum(['high', 'medium', 'low']);

export type Priority = z.infer<typeof PrioritySchema>;

// ============================================================
// Participant Metrics
// ============================================================

export const ParticipantStatsSchema = z.object({
  name: z.string(),
  speakingTime: z.number(),           // Temps de parole en secondes
  speakingPercentage: z.number(),     // % du temps total
  interventionCount: z.number(),      // Nombre de prises de parole
  averageInterventionLength: z.number(), // Durée moyenne d'intervention en secondes
});

export type ParticipantStats = z.infer<typeof ParticipantStatsSchema>;

export const ParticipantMetricsSchema = z.object({
  totalDuration: z.number(),          // Durée totale en secondes
  participants: z.array(ParticipantStatsSchema),
});

export type ParticipantMetrics = z.infer<typeof ParticipantMetricsSchema>;

// ============================================================
// Report Elements
// ============================================================

export const ReportActionSchema = z.object({
  title: z.string(),
  context: z.string(),
  owners: z.array(z.string()),
  priority: PrioritySchema.optional(),
  timestampStart: z.number().optional(), // Position en secondes dans la transcription
});

export type ReportAction = z.infer<typeof ReportActionSchema>;

export const ReportDecisionSchema = z.object({
  title: z.string(),
  context: z.string(),
  owners: z.array(z.string()),
  priority: PrioritySchema.optional(),
  timestampStart: z.number().optional(),
});

export type ReportDecision = z.infer<typeof ReportDecisionSchema>;

export const ReportDeadlineSchema = z.object({
  title: z.string(),
  context: z.string(),
  date: z.string(),
  owners: z.array(z.string()),
  priority: PrioritySchema.optional(),
  timestampStart: z.number().optional(),
});

export type ReportDeadline = z.infer<typeof ReportDeadlineSchema>;

export const ReportKeyPointSchema = z.object({
  title: z.string(),
  context: z.string(),
});

export type ReportKeyPoint = z.infer<typeof ReportKeyPointSchema>;

export const ReportTopicSchema = z.object({
  title: z.string(),
  context: z.string(),
  keyPoints: z.array(ReportKeyPointSchema),
  timestampStart: z.number().optional(),
});

export type ReportTopic = z.infer<typeof ReportTopicSchema>;

// ============================================================
// New: Questions & Risks
// ============================================================

export const QuestionStatusSchema = z.enum(['open', 'answered', 'deferred']);

export type QuestionStatus = z.infer<typeof QuestionStatusSchema>;

export const ReportQuestionSchema = z.object({
  question: z.string(),
  context: z.string(),
  askedBy: z.string().optional(),
  status: QuestionStatusSchema,
  timestampStart: z.number().optional(),
});

export type ReportQuestion = z.infer<typeof ReportQuestionSchema>;

export const RiskSeveritySchema = z.enum(['low', 'medium', 'high']);

export type RiskSeverity = z.infer<typeof RiskSeveritySchema>;

export const ReportRiskSchema = z.object({
  title: z.string(),
  context: z.string(),
  severity: RiskSeveritySchema,
  mitigation: z.string().optional(),
  owners: z.array(z.string()).optional(),
  timestampStart: z.number().optional(),
});

export type ReportRisk = z.infer<typeof ReportRiskSchema>;

// ============================================================
// Meeting Report
// ============================================================

export const MeetingReportSchema = z.object({
  // Metadata
  meetingType: MeetingTypeSchema.optional(), // Type détecté automatiquement

  // Core content
  subject: z.string(),
  summary: z.string(),

  // Structured elements
  topics: z.array(ReportTopicSchema),
  actions: z.array(ReportActionSchema),
  decisions: z.array(ReportDecisionSchema),
  deadlines: z.array(ReportDeadlineSchema),

  // New elements
  questions: z.array(ReportQuestionSchema).optional(),
  risks: z.array(ReportRiskSchema).optional(),

  // Analytics
  participantMetrics: ParticipantMetricsSchema.optional(),
});

export type MeetingReport = z.infer<typeof MeetingReportSchema>;

// ============================================================
// Summarize Meeting Result
// ============================================================

export const SummarizeMeetingResultSchema = z.object({
  recordingId: z.string(),
  report: MeetingReportSchema,
});

export type SummarizeMeetingResult = z.infer<
  typeof SummarizeMeetingResultSchema
>;
